/**
 * TeamApplications — Close Portal team-scoped applications view.
 *
 * For the selected team (linked to an LDAP group) it shows:
 *   1. Team's Published Applications     — published apps whose App Builder,
 *      GRC Control Owner, or Process Owner is a member of the team's LDAP group.
 *   2. Team's Unpublished AI Applications — unpublished AI apps whose app_owner
 *      is a member of the team's LDAP group.
 *
 * "The team" is resolved once via `listLdapGroupMembers` (Brass), then apps are
 * filtered by simple set membership — no per-owner PAPI lookups. Card rendering,
 * pagination, favorites, and ratings reuse the same helpers as the marketplace.
 *
 * Props:
 *   ldapGroup   {string}  the team's LDAP group identifier (owner_identifier)
 *   teamName    {string}  display name for the team (owner_name)
 *   stage       {string}  current stage (for the Brass call)
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import {
  Box,
  Cards,
  Container,
  Header,
  Icon,
  Pagination,
  SpaceBetween,
} from "@amzn/awsui-components-react";

import "./homepage.scss";

import SkeletonCard from "./SkeletonCard";
import { appCardDefinition } from "src/generic-components/AppCard";
import { TEMPLATES } from "src/Ateam-BI/Templates/Constants";
import { formatRunCount } from "src/generic-components/appCardUtils";

import { fetchApps } from "../Ateam-BI/Templates/Utils/AppActivityUtils";
import {
  filterAppsByOwnerSet,
  filterUnpublishedByOwnerSet,
} from "../Ateam-BI/Templates/Utils/MyApplicationsUtils";
import { listLdapGroupMembers } from "../utilities/CloseAPIs";
import {
  listFavorites,
  addFavorite,
  delFavorite,
  getFavorite,
} from "../utilities/Favorites";
import { listFeatured } from "src/utilities/Featured";
import { getUserRatings, submitRating, deleteRating } from "src/utilities/RatingAPIs";
import { getAutomatedAppsByStatus } from "src/utilities/AutomatedAppsAPIs";
import { getLocalCache, setLocalCache } from "src/utilities/CacheHelper";
import { TEAM_APPS_CACHE_KEY, cacheVariables } from "src/AppConstants/CacheConstants";

// 3 cards per row; page 2 rows (6 cards) at a time for the published section.
const PUBLISHED_PAGE_SIZE = 6;
const UNPUBLISHED_PAGE_SIZE = 2;

const cacheKeyFor = (ldapGroup) =>
  `${TEAM_APPS_CACHE_KEY}:${(ldapGroup || "").toLowerCase()}`;
const appKey = (item) => `${item.resource_type}#${item.resource_id}`;

function TeamApplications({ ldapGroup, teamName, stage }) {
  const history = useHistory();
  const userId = useSelector((globalState) => globalState.auth.userId);
  const currentStage = useSelector((globalState) => globalState.auth.currentStage);
  const effectiveStage = stage || currentStage;

  const [loading, setLoading] = useState(true);
  const [publishedApps, setPublishedApps] = useState([]);
  const [unpublishedApps, setUnpublishedApps] = useState([]);

  const [favoritesMap, setFavoritesMap] = useState({});
  const [featuredMap, setFeaturedMap] = useState({});
  const [ratingsMap, setRatingsMap] = useState({});
  const [favDisabled, setFavDisabled] = useState(false);

  const [publishedPageIndex, setPublishedPageIndex] = useState(1);
  const [unpublishedPageIndex, setUnpublishedPageIndex] = useState(1);

  const getUrl = useCallback((item) => (item?.url ? item.url : `/App/${item.app_id}`), []);

  // ---- Favorites / featured / ratings (same patterns as My Page) -----------
  const loadFavorites = useCallback(() => {
    listFavorites(
      userId,
      (result) => {
        const items = result?.data?.listUserPreferences?.items || [];
        const map = {};
        items.forEach((item) => { map[item.preference_identifier] = true; });
        setFavoritesMap(map);
        setFavDisabled(false);
      },
      () => {}
    );
  }, [userId]);

  const loadFeatured = useCallback(() => {
    listFeatured(
      (result) => {
        const items = result?.data?.listUserPreferences?.items || [];
        const map = {};
        items.forEach((item) => { map[item.preference_identifier] = true; });
        setFeaturedMap(map);
      },
      () => {}
    );
  }, []);

  const loadRatings = useCallback(() => {
    if (!userId) return;
    getUserRatings(
      "APP",
      (result) => {
        const ratings = result?.data?.getUserRatings || [];
        const map = {};
        ratings.forEach(({ entity_id, rating }) => { map[entity_id] = rating; });
        setRatingsMap(map);
      },
      () => {}
    );
  }, [userId]);

  const favorite = useCallback(
    (appId) => {
      setFavDisabled(true);
      getFavorite(
        appId,
        userId,
        async (result) => {
          const isFav = result?.data?.getUserPreferences?.preference_identifier === appId;
          if (isFav) {
            await delFavorite(appId, userId, () => {
              setFavoritesMap((prev) => { const u = { ...prev }; delete u[appId]; return u; });
            }, () => {});
          } else {
            await addFavorite(appId, userId, "default", () => {
              setFavoritesMap((prev) => ({ ...prev, [appId]: true }));
            }, () => {});
          }
          setFavDisabled(false);
        },
        () => setFavDisabled(false)
      );
    },
    [userId]
  );

  const handleRatingChange = useCallback((appId, rating) => {
    setRatingsMap((prev) => ({ ...prev, [appId]: rating }));
    if (rating === 0) {
      deleteRating(appId, "APP", () => {}, () => {
        setRatingsMap((prev) => { const c = { ...prev }; delete c[appId]; return c; });
      });
    } else {
      submitRating(appId, "APP", rating, () => {}, () => {
        setRatingsMap((prev) => { const c = { ...prev }; delete c[appId]; return c; });
      });
    }
  }, []);

  // ---- Load: resolve members, then filter apps -----------------------------
  useEffect(() => {
    let cancelled = false;
    if (!ldapGroup) {
      setLoading(false);
      setPublishedApps([]);
      setUnpublishedApps([]);
      return undefined;
    }

    loadFavorites();
    loadFeatured();
    loadRatings();

    // The cache stores ONLY the team's member alias list (small) — never app
    // objects (which would blow the localStorage quota). App objects are always
    // fetched fresh and filtered against the member set; the cache just lets us
    // skip the LDAP members lookup on repeat visits.
    const cacheConf = cacheVariables[TEAM_APPS_CACHE_KEY];
    const cachedMembers =
      cacheConf?.useCache ? getLocalCache(cacheKeyFor(ldapGroup), cacheConf.expiry) : null;

    setLoading(true);

    const run = async () => {
      // 1) Resolve the team's LDAP members — from cache if available, else live.
      let members = cachedMembers;
      if (!Array.isArray(members)) {
        members = await listLdapGroupMembers(ldapGroup, effectiveStage);
        if (cancelled) return;
        // listLdapGroupMembers returns null on a transient failure (vs. [] for a
        // genuinely empty group). Only cache a real, successful lookup — caching
        // a failure would serve an empty team view for the whole expiry window
        // even after the backend recovers.
        if (members === null) {
          members = []; // treat as failure: proceed empty this render, don't cache
        } else if (cacheConf?.useCache) {
          setLocalCache(cacheKeyFor(ldapGroup), members);
        }
      }
      const memberSet = new Set(members);

      if (memberSet.size === 0) {
        setPublishedApps([]);
        setUnpublishedApps([]);
        setLoading(false);
        return;
      }

      // 2) Fetch published + unpublished in parallel, then filter by membership.
      const [apps, unpublishedResult] = await Promise.all([
        fetchApps().catch(() => []),
        new Promise((resolve) => {
          getAutomatedAppsByStatus(
            "UNPUBLISHED",
            (result) => {
              if (result?.loading) return; // wait for the final, complete page
              const raw = result?.data?.getAutomatedAppsByStatus;
              const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
              resolve(parsed?.items || parsed?.Items || parsed || []);
            },
            () => resolve([])
          );
        }),
      ]);
      if (cancelled) return;

      const published = filterAppsByOwnerSet(apps || [], memberSet);
      const unpublishedAi = (unpublishedResult || []).filter(
        (i) => i.template_id === TEMPLATES.AI
      );
      const unpublished = filterUnpublishedByOwnerSet(unpublishedAi, memberSet);

      setPublishedApps(published);
      setUnpublishedApps(unpublished);
      setLoading(false);
    };

    run().catch((error) => {
      console.error("TeamApplications: failed to load team apps", error);
      if (!cancelled) {
        setPublishedApps([]);
        setUnpublishedApps([]);
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [ldapGroup, effectiveStage, loadFavorites, loadFeatured, loadRatings]);

  // ---- Derived pagination --------------------------------------------------
  const publishedPagesCount = Math.max(1, Math.ceil(publishedApps.length / PUBLISHED_PAGE_SIZE));
  const unpublishedPagesCount = Math.max(1, Math.ceil(unpublishedApps.length / UNPUBLISHED_PAGE_SIZE));

  useEffect(() => {
    if (publishedPageIndex > publishedPagesCount) setPublishedPageIndex(publishedPagesCount);
  }, [publishedPageIndex, publishedPagesCount]);
  useEffect(() => {
    if (unpublishedPageIndex > unpublishedPagesCount) setUnpublishedPageIndex(unpublishedPagesCount);
  }, [unpublishedPageIndex, unpublishedPagesCount]);

  const publishedPageItems = useMemo(
    () => publishedApps.slice((publishedPageIndex - 1) * PUBLISHED_PAGE_SIZE, publishedPageIndex * PUBLISHED_PAGE_SIZE),
    [publishedApps, publishedPageIndex]
  );
  const unpublishedPageItems = useMemo(
    () => unpublishedApps.slice((unpublishedPageIndex - 1) * UNPUBLISHED_PAGE_SIZE, unpublishedPageIndex * UNPUBLISHED_PAGE_SIZE),
    [unpublishedApps, unpublishedPageIndex]
  );

  const cardDefinition = useMemo(
    () =>
      appCardDefinition({
        favoritesMap,
        favDisabled,
        favorite,
        ratingsMap,
        handleRatingChange,
        getUrl,
        history,
        featuredMap,
      }),
    [favoritesMap, favDisabled, favorite, ratingsMap, handleRatingChange, getUrl, history, featuredMap]
  );

  const label = teamName || "Team";

  const centeredPager = (pageIndex, pagesCount, onChange) =>
    pagesCount > 1 && (
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Pagination currentPageIndex={pageIndex} pagesCount={pagesCount} onChange={onChange} openEnd={false} />
      </div>
    );

  return (
    <SpaceBetween size="l">
      {/* Team's Published Applications */}
      <Container
        header={
          <Header
            variant="h2"
            counter={!loading ? `(${publishedApps.length})` : undefined}
            description={`Applications owned by ${label}`}
          >
            {label}'s Published Applications
          </Header>
        }
      >
        {loading ? (
          <Box padding={{ top: "l" }}><SkeletonCard count={3} /></Box>
        ) : (
          <SpaceBetween size="m">
            <Cards
              className="apps-cards-container"
              cardDefinition={cardDefinition}
              cardsPerRow={[{ minWidth: 900, cards: 3 }]}
              items={publishedPageItems}
              loadingText="Loading resources"
              empty={
                <Box textAlign="center" color="inherit">
                  <b>No Published Applications</b>
                  <Box padding={{ bottom: "s" }} variant="p" color="inherit">
                    No applications owned by this team to display.
                  </Box>
                </Box>
              }
            />
            {centeredPager(publishedPageIndex, publishedPagesCount, ({ detail }) => setPublishedPageIndex(detail.currentPageIndex))}
          </SpaceBetween>
        )}
      </Container>

      {/* Team's Unpublished AI Applications */}
      <Container
        header={
          <Header
            variant="h2"
            counter={!loading ? `(${unpublishedApps.length})` : undefined}
            description={`Unpublished AI applications across ${label}`}
          >
            {label}'s Unpublished AI Applications
          </Header>
        }
      >
        {loading ? (
          <Box padding={{ top: "l" }}><SkeletonCard count={2} /></Box>
        ) : unpublishedApps.length === 0 ? (
          <Box textAlign="center" color="inherit" padding={{ top: "l" }}>
            <b>No unpublished applications</b>
            <Box variant="p" color="inherit">This team has no unpublished apps.</Box>
          </Box>
        ) : (
          <SpaceBetween size="m">
            <div className="unpublished-cards-container">
              {unpublishedPageItems.map((item) => (
                <div key={appKey(item)} className="unpublished-card-item unpublished-card-item--readonly">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "16px", fontWeight: 700 }}>{item.app_name}</span>
                      {item.template_id === TEMPLATES.AI && (
                        <span className="ai-powered-badge"><Icon name="gen-ai" /> AI-Powered</span>
                      )}
                    </div>
                    <span style={{ backgroundColor: "#fef3e8", color: "#d4760a", padding: "4px 12px", borderRadius: "8px", fontSize: "13px", fontWeight: 600 }}>Pending Review</span>
                  </div>
                  <Box color="text-body-secondary" margin={{ bottom: "xs" }}>{item.app_description}</Box>
                  <Box fontSize="body-s">
                    <SpaceBetween direction="horizontal" size="l">
                      <span><span style={{ color: "#5f6b7a" }}>Created:</span> {item.created_at}</span>
                      <span><span style={{ color: "#5f6b7a" }}>Last Modified:</span> {item.last_updated_at}</span>
                      <span><span style={{ color: "#5f6b7a" }}>App Builder:</span> {item.app_owner}</span>
                      {item.template_id === TEMPLATES.AI && formatRunCount(item.run_count) && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                          <span>▶</span>
                          <span style={{ color: "#5f6b7a" }}>{formatRunCount(item.run_count)} runs</span>
                        </span>
                      )}
                    </SpaceBetween>
                  </Box>
                </div>
              ))}
            </div>
            {centeredPager(unpublishedPageIndex, unpublishedPagesCount, ({ detail }) => setUnpublishedPageIndex(detail.currentPageIndex))}
          </SpaceBetween>
        )}
      </Container>
    </SpaceBetween>
  );
}

export default TeamApplications;
